'use client'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import { useCurrentUser } from '@/hooks/use-current-user'
import { Button } from '@/shared/ui/button'
import { cn } from '@/utils/cn'
import Image from 'next/image'
import React, { HTMLAttributes, useEffect, useState } from 'react'
import { toast } from 'sonner'
// TODO: views에서 꺼내오는 것들 위치 정리 필요
import { useCreateNotification } from '../../views/product-detail/hooks/use-create-notification'
import { ConfirmEmailSendDialog } from '../../views/product-detail/components/confirm-email-send-dialog'
import { usePrevious } from '@uidotdev/usehooks'
import { useDrawerStore } from '@/providers/drawer-store-provider'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
export interface NotificationCreateDrawerProps
  extends HTMLAttributes<HTMLDivElement> {
  trigger?: React.ReactNode
}

export const NotificationCreateDrawer = ({
  className,
  trigger,
  ...props
}: NotificationCreateDrawerProps) => {
  const { open, onOpenChange, productId, price, priceLowest } = useDrawerStore(
    (state) => state,
  )
  const { user } = useCurrentUser()

  const hasEmailValidated = !!user?.emailNoti

  const [select, setSelect] = useState<
    '현재가' | '2주간 최저가' | '역대 최저가' | '직접 설정'
  >('현재가')

  const previousSelect = usePrevious(select)

  const { mutate } = useCreateNotification()
  const [customPrice, setCustomPrice] = useState(price)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (select !== '직접 설정') {
      if (isNaN(customPrice)) {
        setCustomPrice(price)
      }
    }
  }, [select, customPrice, price])

  const handleCustomPriceChange = (value: string) => {
    const numericValue = parseInt(value, 10)
    if (numericValue < 0) {
      setMessage('가격은 0보다 작을 수 없습니다.')
    } else if (numericValue > price) {
      setMessage(
        `가격은 현재가(${price.toLocaleString()} 원)보다 클 수 없습니다.`,
      )
    } else {
      setMessage('')
      setCustomPrice(numericValue)
    }
  }

  const isValid = React.useMemo(
    () =>
      select !== '직접 설정' ||
      (!message && customPrice >= 0 && customPrice <= price),
    [select, message, customPrice, price],
  )

  const priceTable = React.useMemo(
    () => ({
      현재가: price - 10,
      '2주간 최저가': priceLowest,
      '역대 최저가': priceLowest,
      '직접 설정': customPrice,
    }),
    [price, priceLowest, customPrice],
  )

  const isCustomPrice = React.useMemo(() => select === '직접 설정', [select])

  const createNotification = React.useCallback(() => {
    if (!isValid) {
      return
    }
    mutate(
      {
        productId,
        price: priceTable[select],
        notiType: 'PRICE_REACH',
      },
      {
        onSuccess: () => {
          toast.success('알림이 설정되었습니다.')
          onOpenChange(false)
        },
        onError: () => {
          toast.error('새로고침 후 다시 시도해주세요')
        },
      },
    )
  }, [isValid, mutate, productId, priceTable, select, onOpenChange])

  return (
    <div className={cn(className)} {...props}>
      <Drawer onOpenChange={onOpenChange} open={open}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent
          aria-describedby="notification-info"
          className="bg-gray-1 transition-all sm:container"
        >
          <DrawerHeader>
            <div className="flex flex-col items-center gap-4 text-center">
              <Image
                src="/icons/상품상세_벨.svg"
                alt="price-alert"
                width={30}
                height={30}
              />
              <h2 className="text-heading-02-bold sm:text-title-01">
                원하는 가격에 알림받기
              </h2>
              <VisuallyHidden>
                <DrawerTitle>원하는 가격에 알림받기</DrawerTitle>
              </VisuallyHidden>
              <div
                id="notification-info"
                className="text-caption-01-medium text-gray-5 sm:text-body-02-medium"
              >
                <p>입력한 가격까지 낮아지면 알림 받기로</p>
                <p>설정한 이메일로 알려드려요.</p>
              </div>
            </div>
          </DrawerHeader>
          <div className="flex flex-col gap-2 px-5">
            {isCustomPrice && (
              <NotificationCustomInputOption
                value={customPrice}
                setValue={handleCustomPriceChange}
                message={message}
              />
            )}
            {!isCustomPrice && (
              <>
                <NotificationSelectOption
                  label="현재가 보다 저렴할때"
                  value={priceTable['현재가']}
                  isSelect={select === '현재가'}
                  onClick={() => setSelect('현재가')}
                />
                <NotificationSelectOption
                  label="2주간 최저가"
                  value={priceTable['2주간 최저가']}
                  isSelect={select === '2주간 최저가'}
                  onClick={() => setSelect('2주간 최저가')}
                />
                <NotificationSelectOption
                  label="역대 최저가"
                  value={priceTable['역대 최저가']}
                  isSelect={select === '역대 최저가'}
                  onClick={() => setSelect('역대 최저가')}
                />
                <NotificationSelectOption
                  label="직접 설정"
                  value={priceTable['직접 설정']}
                  onClick={() => setSelect('직접 설정')}
                />
              </>
            )}
          </div>
          <DrawerFooter>
            <div className="flex gap-2">
              {isCustomPrice && (
                <Button
                  onClick={() => setSelect(previousSelect)}
                  variant={'secondary'}
                  className="w-full py-4"
                >
                  돌아가기
                </Button>
              )}
              {!isCustomPrice && (
                <DrawerClose asChild>
                  <Button variant={'secondary'} className="w-full py-4">
                    취소
                  </Button>
                </DrawerClose>
              )}

              <DrawerClose asChild>
                <>
                  {hasEmailValidated && (
                    <Button
                      onClick={createNotification}
                      disabled={!isValid}
                      variant={'primary'}
                      className="w-full py-4"
                    >
                      설정 완료
                    </Button>
                  )}
                  {!hasEmailValidated && (
                    <ConfirmEmailSendDialog
                      trigger={
                        <Button
                          disabled={!isValid}
                          variant={'primary'}
                          className="w-full py-4"
                        >
                          설정 완료
                        </Button>
                      }
                    />
                  )}
                </>
              </DrawerClose>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}

const NotificationSelectOption = ({
  label,
  value,
  onClick,
  isSelect,
}: {
  label: string
  value: string | number
  isSelect?: boolean
  onClick?: () => void
}) => {
  return (
    <div
      onClick={onClick}
      className="cursor-pointer rounded-xl border bg-white p-5"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'relative h-5 w-5 rounded-full border border-gray-5',
              isSelect && 'bg-primary',
            )}
          >
            {isSelect && (
              <div
                className={cn(
                  'absolute left-1/2 top-1/2 z-50 h-2 w-2 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-white',
                )}
              />
            )}
          </div>
          <span className="text-body-02-medium text-gray-5">{label}</span>
        </div>
        <span className="text-body-01-semi">{value.toLocaleString()} 원</span>
      </div>
    </div>
  )
}

const NotificationCustomInputOption = ({
  value,
  setValue,
  message,
}: {
  value: string | number
  setValue: (value: string) => void
  message?: string
}) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-1">
        <Input
          autoFocus
          step={1000}
          type="number"
          className="border-none p-0 text-right focus-visible:ring-0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <span className="text-body-01-semi">원</span>
      </div>

      {message && <p className="text-right text-xs text-red-500">{message}</p>}
    </div>
  )
}
